import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { z } from 'zod';
import { useOtpLogin } from '../hooks/auth-actions.js';
import { describeError, mergeFormClasses } from './shared.js';
const emailSchema = z.string().trim().min(1, 'Enter your email.').email('Enter a valid email.');
const codeSchema = z.string().trim().min(4, 'Enter the code from your email.');
/**
 * Two-step passwordless login: the form first asks for an email, then renders
 * a code field after the server responds with a flow id. The progression is
 * managed locally so consumer apps don't have to thread state across pages.
 */
export function OtpForm(props) {
    const classes = mergeFormClasses(props.classNames);
    const { initiate, complete } = useOtpLogin();
    const [email, setEmail] = React.useState('');
    const [code, setCode] = React.useState('');
    const [flowId, setFlowId] = React.useState(null);
    const [validationError, setValidationError] = React.useState(null);
    const error = describeError(initiate.error ?? complete.error, props.errorMessages);
    const handleSendCode = async (event) => {
        event.preventDefault();
        const parsed = emailSchema.safeParse(email);
        if (!parsed.success) {
            setValidationError(parsed.error.issues[0]?.message ?? 'Enter a valid email.');
            return;
        }
        setValidationError(null);
        try {
            const result = await initiate.run({ email: parsed.data });
            setFlowId(result.flowId);
        }
        catch {
            // Error captured by the hook.
        }
    };
    const handleSubmitCode = async (event) => {
        event.preventDefault();
        if (flowId === null)
            return;
        const parsed = codeSchema.safeParse(code);
        if (!parsed.success) {
            setValidationError(parsed.error.issues[0]?.message ?? 'Enter the code.');
            return;
        }
        setValidationError(null);
        try {
            const outcome = await complete.run({ flowId, email, code: parsed.data });
            if (outcome.status === 'authenticated') {
                props.onSuccess(outcome.result);
            }
            else if (props.onMfaRequired !== undefined) {
                props.onMfaRequired(outcome.challenge);
            }
        }
        catch {
            // Error captured by the hook.
        }
    };
    if (flowId === null) {
        return (_jsxs("form", { className: classes.form, onSubmit: handleSendCode, noValidate: true, children: [_jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-otp-email", children: "Email" }), _jsx("input", { id: "auth-ui-otp-email", className: classes.input, type: "email", autoComplete: "email", required: true, value: email, onChange: (event) => setEmail(event.target.value) }), validationError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: validationError })) : null] }), error !== null ? (_jsx("p", { className: classes.error, role: "alert", children: error })) : null, _jsx("button", { type: "submit", className: classes.button, disabled: initiate.loading, children: initiate.loading ? 'Sending…' : 'Send code' })] }));
    }
    return (_jsxs("form", { className: classes.form, onSubmit: handleSubmitCode, noValidate: true, children: [_jsxs("p", { className: classes.hint, children: ["We sent a one-time code to ", email, "."] }), _jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-otp-code", children: "Code" }), _jsx("input", { id: "auth-ui-otp-code", className: classes.input, inputMode: "numeric", autoComplete: "one-time-code", required: true, value: code, onChange: (event) => setCode(event.target.value) }), validationError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: validationError })) : null] }), error !== null ? (_jsx("p", { className: classes.error, role: "alert", children: error })) : null, _jsx("button", { type: "submit", className: classes.button, disabled: complete.loading, children: complete.loading ? 'Verifying…' : 'Verify code' }), _jsx("button", { type: "button", className: classes.link, onClick: () => {
                    setFlowId(null);
                    setCode('');
                }, children: "Use a different email" })] }));
}
//# sourceMappingURL=OtpForm.js.map